import { getMediaPlaylistMetadata } from "../../media/types/index.js";
import { getCodecFamilies, getCodecFamily } from "../../media/utils/tracks.js";
import "../../media/drm.js";
//#region src/playback/primitives/selection-rules.ts
/**
* Apply rules to a candidate list in order; the pick is the first survivor. Two responsibilities the rules don't carry:
* a rule that returns nothing is skipped (fall-through — a preference never empties the set), and once one survivor
* remains the chain stops (early-bail — later rules, including the bandwidth ranker, never run, so the effect doesn't
* subscribe to their signals while the choice is fixed).
*
* @param rules - Rules to apply, most authoritative first
* @param tracks - Candidate tracks
* @param deps - The behavior's `{ state, context, config }`, passed through to each rule
* @returns The surviving candidates, pick first
*/
function applyRules(rules, tracks, deps) {
	let current = tracks;
	for (const rule of rules) {
		const remaining = rule(current, deps);
		if (remaining.length === 0) continue;
		current = remaining;
		if (current.length === 1) break;
	}
	return current;
}
/**
* Apply hard constraints to a candidate list — the pre-pass that runs before the rule chain. A constraint shares a
* rule's signature but its exclusion is _hard_: it removes the unplayable (a codec the environment can't decode, a CDN
* in failover cooldown) and a removed track is never attempted. Unlike `applyRules`, this never skips an empty result
* and never early-bails — every constraint always applies, and an empty survivor set is a real outcome ("nothing
* playable here"), not a fall-through. Because each constraint only removes, the order they run in can't change which
* tracks survive — though one that also _reports_ reads the list at its own position, so placement matters.
*
* @param constraints - Constraints to apply, in order
* @param tracks - Candidate tracks
* @param deps - The behavior's `{ state, context, config }`, passed to each constraint
* @returns The playable survivors (possibly empty)
*/
function applyConstraints(constraints, tracks, deps) {
	let current = tracks;
	for (const constraint of constraints) current = constraint(current, deps);
	return current;
}
/**
* Whether two candidate sets hold the same tracks, by id.
*
* The `equals` both selection behaviors give their candidate-set `computed`. A live playlist refresh swaps in a new
* presentation object carrying the same variants, and a constraint's own inputs can churn without changing which tracks
* survive; in both cases the set is unchanged and the reaction must not re-fire. Compares by id rather than array
* identity for exactly that.
*/
function sameCandidateSet(a, b) {
	return a.length === b.length && a.every((track) => b.some((other) => other.id === track.id));
}
/**
* Capability constraint — a _hard_ filter for the {@link applyConstraints} pre-pass. Removes renditions this
* environment can't decode, probed via the injected `canPlayTrack` (codec → `MediaSource.isTypeSupported`, plus the
* container and encryption assertions that probe can't make). Constraining here — before selection — means an
* unplayable variant is pruned upstream and never picked, instead of surviving into the pipeline to fail late at
* `createSourceBuffer`. That late throw stays as a defensive structural guarantee; with this constraint it should
* rarely fire.
*
* Lives here rather than beside `switchVideoTrack` for the reason this module exists: both the re-evaluating variant
* and the pinned `selectVideoTrack` apply it, and reaching it through `behaviors/track-switching.ts` would drag the ABR
* path into a composition that deliberately omits it.
*
* The probe is handed the whole config alongside each track, so an extended probe reads the props it defines off it
* rather than closing over them — `canPlayTrackWithDrm` reads `drm` and `keySystems` that way.
*
* Passes everything through when there's no probe (a composition that didn't wire one, or DOM-free tests). When it
* prunes _every_ track, the empty result is preserved (per `applyConstraints`) — "nothing playable" — which each
* consuming behavior answers by clearing its selection; reporting the verdict is separate.
*/
function excludeUnplayableTracks(tracks, { config }) {
	const canPlayTrack = config?.canPlayTrack;
	if (!canPlayTrack) return tracks;
	return tracks.filter((track) => canPlayTrack(track, config));
}
/**
* Refused-key-system constraint — a _hard_ filter for the {@link applyConstraints} pre-pass. Removes encrypted
* renditions once negotiation has come back empty-handed.
*
* The late half of DRM pruning. `canPlayTrack`'s DRM variant already drops encrypted renditions no _configured_ key
* system serves, but it runs before the CDM has been asked, so a rendition naming a configured license server survives
* it and only negotiation reveals the CDM is absent. This constraint is that answer arriving: with `NO_KEY_SYSTEM`
* published, no encrypted rendition can decode, whatever it declares.
*
* Reporting is deliberately not here. Pruning to empty is what `track-switching` already reports as
* `SVTA_NO_SUPPORTED_{VIDEO,AUDIO}_TRACK`, so the verdict stays its owner's — a type keeping any clear rendition is
* unaffected and reports nothing, exactly as before. `setupMediaKeys` still reports the _cause_ (4008).
*
* `negotiatedKeySystem` is read through a cast for the same reason `screenResolutionCap` reads `screenResolution`: the
* slot exists only in DRM compositions, and constraining the rule's `State` generic would make every chain that doesn't
* carry it a weak-type mismatch. Read reactively, so publishing the sentinel re-fires the chain.
*/
function excludeRefusedKeySystems(tracks, { state }) {
	if (state.negotiatedKeySystem?.get() !== "none") return tracks;
	return tracks.filter((track) => !getMediaPlaylistMetadata(track)?.encrypted);
}
/**
* The codec families {@link preferCodecFamilies} narrows to when the config carries no `preferredCodecs`: AVC (both its
* 4CCs) and AAC. The default is deliberate rather than neutral — SPF implements no `SourceBuffer.changeType()`, so on a
* mixed-codec source the _initial_ family choice is the one ABR lives inside for the whole source (see
* `stickToSelectedCodecs` in `behaviors/track-switching.ts`), and AVC/AAC is the family pair with the broadest decode
* support. The cost, equally deliberate: on a ladder whose higher rungs are HEVC-only (a common 4K shape), the default
* caps quality at the top AVC rung — configure `preferredCodecs` (or pass `[]`) to trade the other way.
*/
const DEFAULT_PREFERRED_CODECS = [
	"avc1",
	"avc3",
	"mp4a"
];
/**
* Codec-family preference — a _soft_ filter (scope) for a selection-rule chain; the `hevc-variant-selection` concern
* from `internal/design/spf/track-switching-model.md`. Narrows to the tracks whose codec families are all in
* `preferredCodecs` (family-compared, so entries may be 4CCs or full codec strings). Soft-filter semantics: a source
* with no preferred-family rendition (an HEVC-only ladder under the AVC default) is left unnarrowed, and a track
* without codecs never matches — on a ladder with no codecs anywhere (DOM-free tests) the rule is inert.
*
* `preferredCodecs` defaults to {@link DEFAULT_PREFERRED_CODECS}; explicitly pass `[]` to disable the preference while
* keeping the rule composed.
*
* "All families" rather than "any" so a muxed rendition is judged as the unit it is: `hvc1,mp4a` must not count as
* preferred just because its audio half matches.
*
* Lives here rather than beside `switchVideoTrack` for the module's usual reason: the pinned `selectVideoTrack` variant
* can compose it without dragging the ABR path in.
*/
function preferCodecFamilies(tracks, { config }) {
	const preferred = config?.preferredCodecs ?? DEFAULT_PREFERRED_CODECS;
	if (!preferred.length) return tracks;
	const preferredFamilies = new Set(preferred.map(getCodecFamily));
	return tracks.filter((track) => {
		const families = getCodecFamilies(track);
		return !!families && families.every((family) => preferredFamilies.has(family));
	});
}
//#endregion
export { DEFAULT_PREFERRED_CODECS, applyConstraints, applyRules, excludeRefusedKeySystems, excludeUnplayableTracks, preferCodecFamilies, sameCandidateSet };
